/**
 * Deciding what is a phone number and what merely looks like one.
 *
 * A previous build turned the insurance policy number `4471-9920` into a
 * dialable link on a traveller's itinerary. Nothing in the test suite noticed;
 * it was found by looking at the page. Confirmation references, policy numbers,
 * booking codes and flight numbers all live in the same paragraphs as real
 * phone numbers, so the rule has to be deliberately conservative:
 *
 *   - a leading `+` with at least 8 digits, or
 *   - at least 10 digits without one.
 *
 * `4471-9920` has eight digits and no `+`, so it fails both. So does a date, a
 * six-figure booking code, and a flight number.
 */

/**
 * Runs of digits and the punctuation people put between them. Requires a digit
 * at each end, so it will not swallow a trailing bracket or hyphen.
 */
const CANDIDATE = /\+?\d[\d\s().-]{5,}\d/g

const digitCount = (text: string): number => (text.match(/\d/g) ?? []).length

export function isDialable(candidate: string): boolean {
  const trimmed = candidate.trim()
  const digits = digitCount(trimmed)
  return trimmed.startsWith('+') ? digits >= 8 : digits >= 10
}

/** `tel:` strips the formatting; the visible text keeps it. */
export function telHref(candidate: string): string {
  const trimmed = candidate.trim()
  const digits = trimmed.replace(/\D/g, '')
  return `tel:${trimmed.startsWith('+') ? '+' : ''}${digits}`
}

export interface TextSegment {
  type: 'text'
  value: string
}

export interface PhoneSegment {
  type: 'phone'
  value: string
  href: string
}

export type LinkifiedSegment = TextSegment | PhoneSegment

/**
 * Splits text into plain runs and dialable numbers.
 *
 * Returns a single text segment when nothing qualifies, which lets callers skip
 * rebuilding a node they do not need to touch.
 */
export function linkifyPhones(text: string): LinkifiedSegment[] {
  const segments: LinkifiedSegment[] = []
  let lastIndex = 0

  for (const match of text.matchAll(CANDIDATE)) {
    const candidate = match[0]
    const index = match.index

    if (!isDialable(candidate)) continue

    if (index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, index) })
    }
    segments.push({ type: 'phone', value: candidate, href: telHref(candidate) })
    lastIndex = index + candidate.length
  }

  if (segments.length === 0) return [{ type: 'text', value: text }]
  if (lastIndex < text.length) segments.push({ type: 'text', value: text.slice(lastIndex) })
  return segments
}
